/**
 * Official App Store + Google Play badges, each a real store link. Staff app
 * (hausverwalter.ai) on the App module, tenant app (Portalzugang.de) on Kundenportal.
 */
import { useLocale, useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";

type StoreApp = "staff" | "portal";

const STORE_LINKS: Record<StoreApp, { readonly appStore: string; readonly googlePlay: string }> = {
  staff: {
    appStore: "https://apps.apple.com/de/app/hausverwalter-ai/id6818630669",
    googlePlay: "https://play.google.com/store/apps/details?id=ai.hausverwalter.app",
  },
  portal: {
    appStore: "https://apps.apple.com/de/app/portalzugang-de/id6818631308",
    googlePlay: "https://play.google.com/store/apps/details?id=de.portalzugang.app",
  },
};

const BADGE_HEIGHT = 40;

const BADGE_LINK: CSSProperties = {
  display: "block",
  borderRadius: 7,
  boxShadow: "0 14px 28px -12px rgba(0,0,0,0.45)",
};

/** Two store badges side by side; badge language follows the page locale. */
export function StoreBadges(props: {
  readonly app: StoreApp;
  readonly style: CSSProperties;
}): ReactNode {
  const t = useTranslations("marketing.storeBadges");
  const lang: "de" | "en" = useLocale().startsWith("en") ? "en" : "de";
  const links = STORE_LINKS[props.app];
  return (
    <div style={{ display: "flex", gap: 10, ...props.style }}>
      <a href={links.appStore} target="_blank" rel="noopener noreferrer" style={BADGE_LINK}>
        <img
          src={`/marketing/logos/app-store-badge-${lang}.svg`}
          alt={t("appStore")}
          height={BADGE_HEIGHT}
          style={{ display: "block", height: BADGE_HEIGHT, width: "auto" }}
        />
      </a>
      <a href={links.googlePlay} target="_blank" rel="noopener noreferrer" style={BADGE_LINK}>
        <img
          src={`/marketing/logos/google-play-badge-${lang}.png`}
          alt={t("googlePlay")}
          height={BADGE_HEIGHT}
          style={{ display: "block", height: BADGE_HEIGHT, width: "auto" }}
        />
      </a>
    </div>
  );
}
